import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getComicBySlug } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/base/ComicCard';
import { getPopularComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-4xl mb-4">😕</p>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">作品未找到</h2>
            <p className="text-sm text-foreground-500">该漫画作品不存在或已被移除</p>
            <Link to="/" className="inline-block mt-4 px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getPopularComics().filter((c) => c.id !== comic.id).slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.updatedAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网站',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author}`}
        description={`韩漫在线观看《${comic.title}》。${comic.description.substring(0, 120)}。免费下拉式阅读，智能预加载，极速流畅体验。`}
        keywords={`${comic.title}, ${comic.author}, ${comic.tags.join(', ')}, 韩漫在线观看, 韩漫网站`}
        canonicalUrl={`/comic/${slug}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="py-6 md:py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full sm:w-[280px]">
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-[3/4]">
                  <img src={comic.cover} alt={`${comic.title} - 韩漫在线观看`} title={`${comic.title} - ${comic.author}`} className="w-full h-full object-cover object-top" />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-400">·</span>
                  <span className="text-sm text-accent-500 flex items-center gap-1">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">{(comic.views / 10000).toFixed(0)}万阅读</span>
                  <span className="text-sm text-foreground-400">·</span>
                  <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">{comic.status}</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5 bg-background-100 rounded-lg p-4 md:p-5">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-5 flex gap-3">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                    <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-foreground-950">章节列表</h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full cursor-pointer whitespace-nowrap hover:bg-secondary-200 transition-colors">
                    正序
                  </button>
                  <button className="px-3 py-1.5 bg-background-100 text-foreground-500 text-xs font-medium rounded-full cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors">
                    倒序
                  </button>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg divide-y divide-background-200">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.slug}`}
                    className="flex items-center justify-between px-4 md:px-5 py-3 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800">{chapter.title}</span>
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-14 pb-14">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">猜你喜欢</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}